import { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, RefreshCw, ShieldCheck, ShieldAlert, Check, AlertTriangle,
  CircleSlash, Zap, Info,
} from 'lucide-react';
import GmailIntegrationCard from './GmailIntegrationCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  connected: {
    label: 'Conectado',
    Icon: Check,
    ring: 'border-emerald-400/40',
    tint: 'text-emerald-300',
    dot: 'bg-emerald-400',
    glow: '0 0 8px rgba(52, 211, 153, 0.7)',
  },
  requires_setup: {
    label: 'Requiere configuración',
    Icon: AlertTriangle,
    ring: 'border-yellow-400/40',
    tint: 'text-yellow-300',
    dot: 'bg-yellow-400',
    glow: '0 0 8px rgba(250, 204, 21, 0.7)',
  },
  not_configured: {
    label: 'No conectado',
    Icon: CircleSlash,
    ring: 'border-white/15',
    tint: 'text-white/50',
    dot: 'bg-white/30',
    glow: 'none',
  },
  error: {
    label: 'Error',
    Icon: Zap,
    ring: 'border-neon-red/50',
    tint: 'text-neon-red',
    dot: 'bg-neon-red',
    glow: '0 0 8px rgba(255, 0, 51, 0.9)',
  },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.not_configured;
  const { Icon } = meta;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 border ${meta.ring} ${meta.tint} font-jetbrains text-[10px] tracking-widest uppercase`}
      data-testid={`status-badge-${status}`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
};

const ConnectionItem = ({ item }) => {
  const meta = STATUS_META[item.status] || STATUS_META.not_configured;
  return (
    <div
      className={`p-4 border ${meta.ring} bg-black/40 backdrop-blur-xl transition-colors`}
      data-testid={`connection-item-${item.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <span
            className={`mt-1.5 w-2 h-2 rounded-full ${meta.dot} flex-shrink-0`}
            style={{ boxShadow: meta.glow }}
          />
          <div className="min-w-0">
            <div className="font-outfit text-sm text-white truncate" data-testid={`connection-name-${item.id}`}>
              {item.name}
            </div>
            {item.message && (
              <p className={`font-jetbrains text-xs mt-1 ${meta.tint}`}>
                {item.message}
              </p>
            )}
            {item.doc_hint && (
              <p className="font-jetbrains text-[10px] text-white/40 mt-1 flex items-start gap-1">
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>{item.doc_hint}</span>
              </p>
            )}
          </div>
        </div>
        <StatusBadge status={item.status} />
      </div>
    </div>
  );
};

const SecurityPanel = ({ open, onClose }) => {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/connections`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSnapshot(data);
    } catch (e) {
      console.error('SecurityPanel load error:', e);
      setError('No se pudo cargar el panel.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const counts = snapshot?.counts || {};

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={onClose}
            data-testid="security-panel-backdrop"
          />
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="fixed top-0 right-0 h-full w-full sm:w-[520px] bg-[#0A0A0A] border-l border-neon-red/30 z-50 flex flex-col shadow-[0_0_60px_rgba(255,0,51,0.2)]"
            data-testid="security-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-neon-red/20 bg-black/40 backdrop-blur-xl">
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-neon-red" />
                <div>
                  <h2 className="font-outfit text-xl font-bold text-white">Seguridad y conexiones</h2>
                  <p className="font-jetbrains text-[10px] text-white/50 tracking-widest uppercase">
                    Estado de cada integración · sin secretos
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={load}
                  data-testid="refresh-security"
                  className="p-2 text-white/60 hover:text-white transition-colors"
                  title="Actualizar"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={onClose}
                  data-testid="close-security-panel"
                  className="p-2 text-white/60 hover:text-white transition-colors"
                  title="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Warning about secrecy */}
              <div className="flex items-start gap-3 p-3 border border-neon-red/20 bg-neon-red/5">
                <ShieldAlert className="w-4 h-4 text-neon-red mt-0.5 flex-shrink-0" />
                <p className="font-jetbrains text-[11px] text-white/70 leading-relaxed">
                  Este panel muestra únicamente estados. Ninguna API key, token,
                  cookie o secreto se envía al frontend. Las credenciales viven
                  exclusivamente en backend/.env.
                </p>
              </div>

              {error && (
                <div className="p-3 border border-neon-red/40 bg-neon-red/10 font-jetbrains text-xs text-neon-red" data-testid="security-error">
                  {error}
                </div>
              )}

              {/* Interactive integrations (Gmail, and future services here) */}
              <section data-testid="security-integrations">
                <h3 className="font-jetbrains text-[10px] text-white/50 tracking-[0.2em] uppercase mb-3">
                  Integraciones
                </h3>
                <div className="space-y-2">
                  <GmailIntegrationCard />
                </div>
              </section>

              {/* Counts */}
              {snapshot && (
                <div className="grid grid-cols-4 gap-2" data-testid="security-counts">
                  {[
                    ['connected', counts.connected || 0],
                    ['requires_setup', counts.requires_setup || 0],
                    ['not_configured', counts.not_configured || 0],
                    ['error', counts.error || 0],
                  ].map(([key, count]) => {
                    const meta = STATUS_META[key];
                    return (
                      <div
                        key={key}
                        className={`p-2 border ${meta.ring} bg-black/40 text-center`}
                        data-testid={`count-${key}`}
                      >
                        <div className={`font-outfit text-lg ${meta.tint}`}>{count}</div>
                        <div className="font-jetbrains text-[9px] text-white/50 tracking-widest uppercase mt-0.5">
                          {meta.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {loading && !snapshot && (
                <p className="font-jetbrains text-xs text-white/50 text-center py-8">
                  Cargando estado...
                </p>
              )}

              {/* Categories */}
              {snapshot?.categories?.map((cat) => (
                <section key={cat.id} data-testid={`security-category-${cat.id}`}>
                  <h3 className="font-jetbrains text-[10px] text-white/50 tracking-[0.2em] uppercase mb-3">
                    {cat.label}
                  </h3>
                  <div className="space-y-2">
                    {cat.items.map((item) => (
                      <ConnectionItem key={item.id} item={item} />
                    ))}
                  </div>
                </section>
              ))}

              {snapshot?.generated_at && (
                <p className="font-jetbrains text-[10px] text-white/30 text-center pt-4">
                  Última comprobación: {new Date(snapshot.generated_at).toLocaleString()}
                </p>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SecurityPanel;
